import React, { useState, useRef } from 'react';
import { Upload, AlertCircle } from 'lucide-react';

export default function ShareYourWork() {
  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const fileInputRef = useRef<HTMLInputElement>(null);

  const uploadFiles = async (selectedFiles: File[]) => {
    setError('');
    setSuccess('');
    setIsUploading(true);
    setUploadProgress(0);

    try {
      const formData = new FormData();
      formData.append('userName', 'Anonymous');
      formData.append('userEmail', 'anonymous@upload.com');

      selectedFiles.forEach((file) => {
        formData.append('files', file);
      });

      const xhr = new XMLHttpRequest();

      // Track upload progress
      xhr.upload.addEventListener('progress', (e) => {
        if (e.lengthComputable) {
          const percentComplete = Math.round((e.loaded / e.total) * 100);
          setUploadProgress(percentComplete);
        }
      });

      // Handle completion
      xhr.addEventListener('load', () => {
        if (xhr.status >= 200 && xhr.status < 300) {
          setSuccess('Files uploaded successfully!');
          setUploadProgress(0);
          setTimeout(() => setSuccess(''), 5000);
        } else {
          const data = JSON.parse(xhr.responseText);
          throw new Error(data.error || 'Upload failed');
        }
        setIsUploading(false);
      });

      // Handle errors
      xhr.addEventListener('error', () => {
        setError('Failed to upload. Please try again.');
        setIsUploading(false);
      });

      xhr.open('POST', '/api/submissions');
      xhr.send(formData);
    } catch (err: any) {
      setError(err.message || 'Failed to upload. Please try again.');
      setIsUploading(false);
    }
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFiles = Array.from(e.target.files || []);
    
    if (selectedFiles.length === 0) {
      return;
    }

    // Start upload immediately
    uploadFiles(selectedFiles);

    // Reset input
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.currentTarget.classList.remove('border-blue-500', 'bg-blue-50');

    const droppedFiles = Array.from(e.dataTransfer.files || []);
    
    if (droppedFiles.length === 0) {
      return;
    }

    // Start upload immediately
    uploadFiles(droppedFiles);
  };

  return (
    <section className="py-12 md:py-16 bg-white border-b border-gray-200">
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Error and Success Messages */}
        {error && (
          <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg flex items-start gap-3">
            <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
            <p className="text-red-700">{error}</p>
          </div>
        )}

        {success && (
          <div className="mb-6 p-4 bg-green-50 border border-green-200 rounded-lg flex items-start gap-3">
            <div className="w-5 h-5 bg-green-600 rounded-full flex items-center justify-center flex-shrink-0">
              <span className="text-white text-sm">✓</span>
            </div>
            <p className="text-green-700">{success}</p>
          </div>
        )}

        <div className="bg-gray-50 rounded-lg border border-gray-200 p-6">
          {/* Upload Button */}
          <div className="text-center">
            <input
              ref={fileInputRef}
              type="file"
              multiple
              accept="image/*,video/*"
              onChange={handleFileSelect}
              className="hidden"
              disabled={isUploading}
            />
            
            {!isUploading ? (
              <div
                onDragOver={(e) => {
                  e.preventDefault();
                  e.currentTarget.classList.add('border-blue-500', 'bg-blue-50');
                }}
                onDragLeave={(e) => {
                  e.currentTarget.classList.remove('border-blue-500', 'bg-blue-50');
                }}
                onDrop={handleDrop}
                className="border-2 border-dashed border-gray-300 rounded-lg p-8 transition-colors cursor-pointer"
              >
                <Upload className="w-16 h-16 mx-auto mb-4 text-blue-600" />
                <button
                  type="button"
                  onClick={() => fileInputRef.current?.click()}
                  className="bg-blue-600 text-white px-8 py-4 rounded-lg font-semibold hover:bg-blue-700 transition text-lg shadow-lg hover:shadow-xl"
                >
                  Choose Files to Upload
                </button>
                <p className="text-gray-600 mt-4">or drag and drop files here</p>
              </div>
            ) : (
              <div className="bg-blue-50 p-8 rounded-lg">
                <div className="flex items-center justify-between mb-3">
                  <span className="text-lg font-medium text-blue-900">Uploading...</span>
                  <span className="text-2xl font-bold text-blue-900">{uploadProgress}%</span>
                </div>
                <div className="w-full bg-blue-200 rounded-full h-3">
                  <div
                    className="bg-blue-600 h-3 rounded-full transition-all duration-300"
                    style={{ width: `${uploadProgress}%` }}
                  />
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}
